import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Check, Trash2, Search, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import sentinelNetwork from '@/lib/api/sentinelNetwork';

export interface ImageEntry {
  key: string;    // full path: "nerd/image-name"
  name: string;   // short: "image-name"
  lastModified?: string | null;
}

interface InlineTrivyImageListProps {
  appId: string;
  savedImages: string[];                        // array of image keys
  onSave: (images: string[]) => Promise<void>;
  disabled?: boolean;
  /** @deprecated No longer used — images are never auto-fetched on mount. */
  autoFetch?: boolean;
}

function formatDate(dateStr?: string | null) {
  if (!dateStr) return '';
  try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
}

export default function InlineTrivyImageList({
  appId,
  savedImages,
  onSave,
  disabled = false,
  autoFetch = false,
}: InlineTrivyImageListProps) {
  const theme = useTheme();

  // Confirmed images in the main list
  const [confirmed, setConfirmed] = useState<ImageEntry[]>([]);
  const [saving, setSaving] = useState(false);

  // Input + Discover flow
  const [query, setQuery] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoverResults, setDiscoverResults] = useState<ImageEntry[]>([]);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discoverSelected, setDiscoverSelected] = useState<Set<string>>(new Set());
  const [discoverError, setDiscoverError] = useState('');

  // On mount: restore saved images if any.
  // Images are never auto-fetched on mount — the user must click Discover to load them.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    if (savedImages.length > 0) {
      setConfirmed(savedImages.map((key): ImageEntry => ({ key, name: key.split('/').pop() || key })));
    }
    // No auto-fetch: list starts empty when there are no saved images.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const discover = async () => {
    setDiscovering(true);
    setDiscoverError('');
    setDiscoverResults([]);
    setDiscoverSelected(new Set());
    try {
      const res = await sentinelNetwork.get('/tools/trivy/images', { params: { appId, q: query.trim() || '' } });
      const raw: ImageEntry[] = (res.data?.images || []);
      // Pre-check images already in the confirmed list
      const confirmedKeys = new Set(confirmed.map((c) => c.key));
      setDiscoverResults(raw);
      setDiscoverSelected(new Set(raw.filter((r) => confirmedKeys.has(r.key)).map((r) => r.key)));
      setDiscoverOpen(true);
    } catch (err: any) {
      setDiscoverError(err?.response?.data?.error || err?.message || 'Discovery failed');
      setDiscoverOpen(true);
    } finally {
      setDiscovering(false);
    }
  };

  const toggleDiscover = (key: string) => {
    setDiscoverSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const confirmDiscovered = async () => {
    const toAdd = discoverResults.filter((r) => discoverSelected.has(r.key));
    const existingKeys = new Set(confirmed.map((c) => c.key));
    const merged = [
      ...confirmed,
      ...toAdd.filter((r) => !existingKeys.has(r.key)),
    ];
    setConfirmed(merged);
    setDiscoverOpen(false);
    setDiscoverResults([]);
    setDiscoverSelected(new Set());
    await doSave(merged);
  };

  const cancelDiscover = () => {
    setDiscoverOpen(false);
    setDiscoverResults([]);
    setDiscoverSelected(new Set());
    setDiscoverError('');
  };

  const removeImage = async (key: string) => {
    const next = confirmed.filter((c) => c.key !== key);
    setConfirmed(next);
    await doSave(next);
  };

  const doSave = async (images: ImageEntry[]) => {
    setSaving(true);
    try {
      await onSave(images.map((i) => i.key));
    } finally {
      setSaving(false);
    }
  };

  const addManual = async () => {
    const key = query.trim();
    if (!key || confirmed.some((c) => c.key === key)) return;
    const entry: ImageEntry = { key, name: key.split('/').pop() || key };
    const next = [...confirmed, entry];
    setConfirmed(next);
    setQuery('');
    await doSave(next);
  };

  return (
    <Box>
      {/* Header */}
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '.05em', display: 'block', mb: 1 }}>
        Container Images
        {confirmed.length > 0 && ` · ${confirmed.length} selected`}
      </Typography>

      {/* Confirmed images */}
      {confirmed.length > 0 && (
        <Stack spacing={0.5} sx={{ mb: 1.25 }}>
          {confirmed.map((img) => (
            <Box key={img.key} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.25, py: 0.6,
              border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.3),
              borderRadius: 1,
              bgcolor: alpha(theme.palette.success.main, 0.04),
            }}>
              <Check size={13} color={theme.palette.success.main} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontSize: 12, fontWeight: 600 }}>
                  {img.name}
                </Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ fontFamily: 'monospace', fontSize: 10, display: 'block' }} noWrap>
                  {img.key}
                </Typography>
              </Box>
              {img.lastModified && (
                <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                  {formatDate(img.lastModified)}
                </Typography>
              )}
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => removeImage(img.key)} disabled={disabled || saving}
                  sx={{ p: 0.25 }}>
                  <Trash2 size={13} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}

      {/* Search + Discover / Add row */}
      {!disabled && (
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search image name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') discover(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={14} style={{ color: theme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 12, py: '5px' } }}
          />
          <Button
            size="small" variant="contained"
            onClick={discover}
            disabled={discovering}
            startIcon={discovering ? <CircularProgress size={12} /> : <Zap size={12} />}
            sx={{ textTransform: 'none', fontSize: 11, px: 1.5, py: '5px', whiteSpace: 'nowrap' }}
          >
            {discovering ? 'Searching…' : 'Discover'}
          </Button>
          <Button
            size="small" variant="outlined"
            onClick={addManual}
            disabled={!query.trim() || saving}
            startIcon={<Check size={12} />}
            sx={{ textTransform: 'none', fontSize: 11, px: 1.25, py: '5px', whiteSpace: 'nowrap' }}
          >
            + Add
          </Button>
        </Box>
      )}

      {/* Discover results panel */}
      <Collapse in={discoverOpen} unmountOnExit>
        <Box sx={{ mt: 1.5, border: '1px solid', borderColor: 'primary.light', borderRadius: 1, overflow: 'hidden' }}>
          {/* Panel header */}
          <Box sx={{
            px: 1.25, py: 0.75,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderBottom: '1px solid', borderColor: 'primary.light',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
          }}>
            <Typography variant="caption" fontWeight={700} color="primary.main"
              sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '.05em', flexShrink: 0 }}>
              Discover Results
              {discoverResults.length > 0 && ` · ${discoverResults.length} found`}
            </Typography>
            {discoverResults.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Button
                  size="small" variant="text"
                  onClick={() => {
                    const allSelected = discoverSelected.size === discoverResults.length;
                    setDiscoverSelected(
                      allSelected ? new Set() : new Set(discoverResults.map((i) => i.key))
                    );
                  }}
                  sx={{ textTransform: 'none', fontSize: 10, py: 0, px: 0.75, minWidth: 0, lineHeight: 1.6 }}
                >
                  {discoverSelected.size === discoverResults.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Chip label={`${discoverSelected.size} to add`} size="small" color="primary"
                  variant="outlined" sx={{ fontSize: 10, height: 18 }} />
              </Box>
            )}
          </Box>

          {/* Error */}
          {discoverError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', px: 1.25, py: 1, fontSize: 11 }}>
              {discoverError}
            </Typography>
          )}

          {/* Results list */}
          {discoverResults.length > 0 && (
            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              {discoverResults.map((img) => {
                const checked = discoverSelected.has(img.key);
                return (
                  <Box key={img.key} onClick={() => toggleDiscover(img.key)} sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.25, py: 0.75,
                    borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.5),
                    cursor: 'pointer',
                    bgcolor: checked ? alpha(theme.palette.primary.main, 0.07) : 'transparent',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    '&:last-child': { borderBottom: 'none' },
                    transition: 'background-color 0.1s',
                  }}>
                    <Checkbox size="small" checked={checked} sx={{ p: 0 }}
                      onClick={(e) => e.stopPropagation()} onChange={() => toggleDiscover(img.key)} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontSize: 12, fontWeight: checked ? 600 : 400 }}>
                        {img.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary"
                        sx={{ fontFamily: 'monospace', fontSize: 10, display: 'block' }} noWrap>
                        {img.key}
                      </Typography>
                    </Box>
                    {img.lastModified && (
                      <Typography variant="caption" sx={{ fontSize: 10, whiteSpace: 'nowrap', color: 'text.disabled' }}>
                        {formatDate(img.lastModified)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {!discoverError && discoverResults.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.25, py: 1.5, fontSize: 11 }}>
              No images found. Try a different search term.
            </Typography>
          )}

          {/* Footer */}
          <Box sx={{
            px: 1.25, py: 0.75,
            borderTop: '1px solid', borderColor: 'primary.light',
            display: 'flex', justifyContent: 'flex-end', gap: 1,
          }}>
            <Button size="small" onClick={cancelDiscover} sx={{ textTransform: 'none', fontSize: 12 }}>
              Cancel
            </Button>
            {discoverResults.length > 0 && (
              <Button size="small" variant="contained" onClick={confirmDiscovered}
                disabled={discoverSelected.size === 0}
                startIcon={<Check size={12} />}
                sx={{ textTransform: 'none', fontSize: 12 }}>
                Add {discoverSelected.size > 0 ? `${discoverSelected.size} ` : ''}Selected
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
